import React, { useEffect, useState } from 'react';
import { ActionButton, Text, MessageBar, MessageBarType, Spinner, SpinnerSize, Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { useModalContext } from './ModalProvider';
import StripePaymentForm from './StripePaymentForm';
import { userAPI } from '../UserAPI';
import styles from './StripePaymentMethod.module.sass';

interface StripePaymentMethodProps {
    customerId: string;
    onPaymentMethodAdded?: (paymentMethod: any) => void;
    onPaymentMethodRemoved?: (paymentMethodId: string) => void;
}

interface PaymentMethod {
    id: string;
    type: string;
    card?: {
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
    };
    billing_details?: {
        name: string;
        email: string;
    };
}

export default function StripePaymentMethod({ customerId, onPaymentMethodAdded, onPaymentMethodRemoved }: StripePaymentMethodProps) {
    const { t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<string | null>(null);
    const [formLoading, setFormLoading] = useState(false);

    useEffect(() => {
        loadPaymentMethods();
    }, [customerId]);

    const loadPaymentMethods = async () => {
        try {
            setLoading(true);
            const data = await userAPI.getPaymentMethods(customerId) as any;
            if (data) {
                setPaymentMethods(data.paymentMethods || []);
                setDefaultPaymentMethod(data.defaultPaymentMethod);
            } else {
                // Handle case where API returns null/undefined
                setPaymentMethods([]);
                setDefaultPaymentMethod(null);
            }
        } catch (error) {
            console.error('Error loading payment methods:', error);
            // Don't show error to user for initial load, just set empty state
            setPaymentMethods([]);
            setDefaultPaymentMethod(null);
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentMethodAdded = (newPaymentMethod: PaymentMethod) => {
        setPaymentMethods(prev => [...prev, newPaymentMethod]);
        onPaymentMethodAdded?.(newPaymentMethod);
        setShowAddForm(false);
        setFormLoading(false);

        // Show success message for temporary payment methods
        if ((newPaymentMethod as any)._temp) {
            console.log('✅ Payment method added successfully (temporary - backend not implemented)');
        }
    };

    const removePaymentMethod = async (paymentMethodId: string) => {
        try {
            setLoading(true);
            await userAPI.removePaymentMethod(customerId, paymentMethodId);
            setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodId));
            onPaymentMethodRemoved?.(paymentMethodId);
        } catch (error) {
            console.error('Error removing payment method:', error);
            pushError(new Error('Failed to remove payment method. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const setDefaultPaymentMethodHandler = async (paymentMethodId: string) => {
        try {
            setLoading(true);
            await userAPI.setDefaultPaymentMethod(customerId, paymentMethodId);
            setDefaultPaymentMethod(paymentMethodId);
        } catch (error) {
            console.error('Error setting default payment method:', error);
            pushError(new Error('Failed to set default payment method. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const formatCardNumber = (last4: string) => `•••• •••• •••• ${last4}`;
    const formatExpiry = (month: number, year: number) => `${month.toString().padStart(2, '0')}/${year}`;

    if (loading && paymentMethods.length === 0) {
        return <Spinner size={SpinnerSize.large} label="Lade Zahlungsmethoden..." />;
    }

    return (
        <div>
            <div className="displayFlex justifyContentBetween alignItemsCenter marginBottomM">
                <ActionButton
                    iconProps={{ iconName: 'Add' }}
                    onClick={() => setShowAddForm(true)}
                    disabled={loading}
                >
                    Zahlungsmethode hinzufügen
                </ActionButton>
            </div>

            {paymentMethods.length === 0 && !loading && (
                <MessageBar messageBarType={MessageBarType.info}>
                    Keine Zahlungsmethoden hinterlegt. Fügen Sie eine Kreditkarte oder Bankverbindung hinzu.
                </MessageBar>
            )}

            {paymentMethods.map((paymentMethod) => (
                <div key={paymentMethod.id} className="borderNeutral paddingM marginBottomS">
                    <div className="displayFlex justifyContentBetween alignItemsCenter gapM">
                        <div className='displayFlex alignItemsCenter gapM'>
                            {paymentMethod.card && (
                                <div className='displayFlex alignItemsCenter gapM'>
                                    <Text variant="medium">
                                        {paymentMethod.card.brand.toUpperCase()} {formatCardNumber(paymentMethod.card.last4)}
                                        {(paymentMethod as any)._temp && (
                                            <span className="colorOrange fontWeightBold marginLeftS">(Temporär)</span>
                                        )}
                                    </Text>
                                    <Text variant="small" className="colorNeutralSecondary">
                                        Gültig bis {formatExpiry(paymentMethod.card.exp_month, paymentMethod.card.exp_year)}
                                    </Text>
                                    {paymentMethod.billing_details?.name && (
                                        <Text variant="small" className="colorNeutralSecondary">
                                            {paymentMethod.billing_details.name}
                                        </Text>
                                    )}
                                    {(paymentMethod as any)._temp && (
                                        <Text variant="small" className="colorOrange">
                                            ⚠️ Backend API noch nicht implementiert - wird nicht gespeichert
                                        </Text>
                                    )}
                                </div>
                            )}
                            {paymentMethod.type === 'sepa_debit' && (
                                <div>
                                    <Text variant="medium">SEPA-Lastschrift</Text>
                                    <Text variant="small" className="colorNeutralSecondary">
                                        Bankkonto hinterlegt
                                    </Text>
                                </div>
                            )}
                        </div>
                        <div className="displayFlex alignItemsCenter gapM">
                            {defaultPaymentMethod === paymentMethod.id && (
                                <Text variant="small" className="colorGreen fontWeightBold">
                                    Standard
                                </Text>
                            )}
                            {defaultPaymentMethod !== paymentMethod.id && (
                                <ActionButton
                                    iconProps={{ iconName: 'CheckMark' }}
                                    onClick={() => setDefaultPaymentMethodHandler(paymentMethod.id)}
                                    disabled={loading}
                                >
                                </ActionButton>
                            )}
                            <ActionButton
                                iconProps={{ iconName: 'Delete' }}
                                onClick={() => removePaymentMethod(paymentMethod.id)}
                                disabled={loading}
                            >
                            </ActionButton>
                        </div>
                    </div>
                </div>
            ))}

            <Dialog
                hidden={!showAddForm}
                onDismiss={() => setShowAddForm(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Zahlungsmethode hinzufügen',
                    subText: 'Fügen Sie eine neue Kreditkarte oder Bankverbindung hinzu.'
                }}
                modalProps={{
                    isBlocking: false,
                    className: styles['wide-dialog']
                }}
            >
                <div style={{ padding: '20px 0' }}>
                    <StripePaymentForm
                        customerId={customerId}
                        onSuccess={handlePaymentMethodAdded}
                        onCancel={() => setShowAddForm(false)}
                        onLoadingChange={setFormLoading}
                    />
                </div>
                <DialogFooter>
                    <DefaultButton onClick={() => setShowAddForm(false)} text="Abbrechen" />
                    <PrimaryButton
                        onClick={() => {
                            // Trigger form submission
                            const form = document.querySelector('form');
                            if (form) {
                                const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                                form.dispatchEvent(submitEvent);
                            }
                        }}
                        disabled={formLoading}
                        text={formLoading ? 'Wird verarbeitet...' : 'Zahlungsmethode hinzufügen'}
                    />
                </DialogFooter>
            </Dialog>
        </div>
    );
} 